import moment from 'moment';
import { t } from 'ttag';

export const MAX_B64_FILE_SIZE = 5242880; // 5MB
export const BACKEND_DATETIME_FORMAT = moment.ISO_8601;
export const MS_TOP_NOTIFICATION_DURATION = 2 ** 12;

// Django username regex
export const R_USERNAME = /^[\w@+.-]+$/i;
// NOTE: Unsafe regex shouldn't matter too much because this is used just for frontend validation, right?
export const R_EMAIL = /^[\w+.-]+@([a-z0-9-]+\.)+[a-z0-9-]{2,63}$/i; // eslint-disable-line security/detect-unsafe-regex
// Backend's UUID4 token
export const R_TOKEN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
// Plain positive integer (including 0), excluding `1e2` syntax, etc.
export const R_POS_INT = /^\d+$/;
// Finnish SSN syntax
export const R_SSN = /^\d{6}[-+A]\d{3}(?![GIOQ])[0-9A-Y]$/i;

// eslint-disable-next-line max-len
export const LEAFLET_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
export const LEAFLET_TILELAYER_URL = 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png?key=cb1_3lt3_1_d6a99190854da32d34f93f64';

export const APP = Object.freeze({ UNAUTHENTICATED: 0, PERSON: 1, OPERATOR: 2 });

// `SERVER` value is prepended to all API calls in src/middleware/api.js
export const SERVER = (process.env.NODE_ENV === 'production' && window.react?.REACT_APP_API_URL)
    ? window.react.REACT_APP_API_URL
    : process.env.REACT_APP_API_URL;

// `APP_NAME` value is used for selecting the login page for PERSON or OPERATOR (both in local)
export const APP_NAME = (process.env.NODE_ENV === 'production' && window.react?.REACT_APP_APP_NAME)
    ? window.react.REACT_APP_APP_NAME
    : process.env.REACT_APP_APP_NAME;

export const PART_SEARCH_URL = (process.env.NODE_ENV === 'production' && window.react?.REACT_APP_PART_SEARCH_URL)
    ? window.react.REACT_APP_PART_SEARCH_URL
    : process.env.REACT_APP_PART_SEARCH_URL;

// `DDS_URL` is the address of the external DDS app, linked from the operator navigation
export const DDS_URL = ((process.env.NODE_ENV === 'production' && window.react?.REACT_APP_DDS_URL)
    ? window.react.REACT_APP_DDS_URL
    : process.env.REACT_APP_DDS_URL) || 'http://localhost:8501';

// `SHOP_ID_MARKER` in endpoint is automatically replaced with the currently selected shop's ID in src/middleware/api.js
export const SHOP_ID_MARKER = ':SHOP_ID';

export const METHODS = Object.freeze(['GET', 'DELETE', 'POST', 'PATCH', 'PUT']);

export const FORCE_LOGOUT_CODES = Object.freeze([
    'REFRESH_TOKEN_EXPIRED',
    'REFRESH_TOKEN_DATA_INVALID',
    'INVALID_HEADER',
]);

export const OPENING_HOURS_FIELDS = Object.freeze([
    'opening_hours_mon',
    'opening_hours_tue',
    'opening_hours_wed',
    'opening_hours_thu',
    'opening_hours_fri',
    'opening_hours_sat',
    'opening_hours_sun',
]);

export const SHOP_BLANK = Object.freeze({
    id: '',
    name: '',
    email: '',
    phone: '',
    opening_hours_mon: '',
    opening_hours_tue: '',
    opening_hours_wed: '',
    opening_hours_thu: '',
    opening_hours_fri: '',
    opening_hours_sat: '',
    opening_hours_sun: '',
    location: {},
    user_ids: [],
    is_private: false,
    has_pickup: false,
    is_dismantler: false,
});

export const DEFAULT_MAP_COORDS = Object.freeze([60.175, 24.925]);

export const COMPANY_BLANK = Object.freeze({
    id: '',
    name: '',
    email: '',
    phone: '',
    contact_person: '',
    location: {},
    users: [],
    shop_ids: [],
});

export const TRAFI_COLORS = Object.freeze({
    Black: '#555',
    Blue: '#59C',
    'Brown (beige)': '#875',
    Dark: '#777',
    Gray: '#888',
    Green: '#5A3',
    Grey: '#888',
    Light: '#CCC',
    Metallic: '#BBB',
    'Multi-coloured': '#5A3',
    Multicolor: '#5A3',
    Orange: '#eb5',
    Purple: '#B7B',
    Red: '#D54',
    Silver: '#BBB',
    Turquoise: '#5AE',
    Violet: '#959',
    White: '#CCC',
    Yellow: '#ED7',
});

export const IMG_EXTS = Object.freeze([
    'apng', //                                  image/apng
    'png', //                                   image/apng || image/png
    'avif', //                                  image/avif
    'bmp', //                                   image/bmp
    'gif', //                                   image/gif
    'ico', 'cur', //                            image/x-icon
    'jpeg', 'jpg', 'jfif', 'pjpeg', 'pjp', //   image/jpeg
    'svg', //                                   image/svg+xml
    'tif', 'tiff', //                           image/tiff
    'webp', //                                  image/webp
]);

export const BATTERY_STATUS = Object.freeze({
    PROVIDER_DRAFT: 'provider_draft', // A battery draft created by a provider
    IMPORTER_DRAFT: 'importer_draft', // A battery draft created by an importer
    PROVIDER_ONSITE: 'provider_onsite', // Provider just created a battery object, but nothing has been done to it yet
    IMPORTER_ONSITE: 'importer_onsite', // Importer just created a battery object, but nothing has been done to it yet
    // REUSE
    // Semantically same as `shop_sold`, but bagend maybe needs this. Reuse process is done.
    RECEIVER_BOUGHT_SHOP: 'receiver_bought_shop',
    // RECYCLE
    PROVIDER_DELIVER_SHOP: 'provider_deliver_shop', // Provider decided to deliver the battery to an operator
    IMPORTER_DELIVER_SHOP: 'importer_deliver_shop', // Importer decided to deliver the battery to an operator
    PROVIDER_DELIVER_IMPORTER: 'provider_deliver_importer', // Provider decided to deliver the battery to an importer
    // An operator rejected the request to pick up the battery from the provider
    SHOP_PICKUP_PROVIDER_REJECT: 'shop_pickup_provider_reject',
    // An operator accepted the request to pick up the battery from the provider
    SHOP_PICKUP_PROVIDER_ACCEPT: 'shop_pickup_provider_accept',
    // An operator rejected the request to pick up the battery from the importer
    SHOP_PICKUP_IMPORTER_REJECT: 'shop_pickup_importer_reject',
    // An operator accepted the request to pick up the battery from the importer
    SHOP_PICKUP_IMPORTER_ACCEPT: 'shop_pickup_importer_accept',
    SHOP_PICKUP_PROVIDER: 'shop_pickup_provider', // An operator is picking up the battery from the provider
    SHOP_PICKUP_IMPORTER: 'shop_pickup_importer', // An operator is picking up the battery from the importer
    SHOP_ONSITE: 'shop_onsite', // An operator is ready to recycle the battery
    SHOP_KEPT: 'shop_kept', // Shop decided to keep the battery. Reuse process is done.
    SHOP_PROCESS: 'shop_process', // An operator is recycling the battery
    SHOP_PUBLISHED: 'shop_published', // Shop decided to publish the battery to the market
    SHOP_SOLD: 'shop_sold', // Shop decided to accept a bid for the battery at the market
    SHOP_DONE: 'shop_done', // An operator has finished recycling the battery. Recycle process is done.
    IMPORTER_PICKUP_PROVIDER: 'importer_pickup_provider', // An importer is picking up the battery from the provider
    FACTORY_PICKUP_PROVIDER: 'factory_pickup_provider', // A factory is picking up the battery from the provider
    FACTORY_PICKUP_IMPORTER: 'factory_pickup_importer', // A factory is picking up the battery from the importer
});

export const BATTERY_CONDITION = Object.freeze({
    REUSE: 'reuse',
    RECYCLE: 'recycle',
    REPAIR: 'repair', // Currently unused
});

export const BATTERY_WHOLENESS = Object.freeze({
    WHOLE: 'whole',
    PARTS: 'parts',
});

// who the battery is sent to - chosen at the start of the recipient selection
export const BATTERY_RECIPIENT_TYPE = Object.freeze({
    OPERATOR: 'operator',
    FACTORY: 'factory',
});

export const BATTERY_TYPE_OPTIONS = () => [
    { value: 'LFP', label: t`(BATTERY_TYPE)_LFP` },
    { value: 'LMO', label: t`(BATTERY_TYPE)_LMO` },
    { value: 'LTO', label: t`(BATTERY_TYPE)_LTO` },
    { value: 'NCA', label: t`(BATTERY_TYPE)_NCA` },
    { value: 'NMC', label: t`(BATTERY_TYPE)_NMC` },
    { value: 'NiMh', label: t`(BATTERY_TYPE)_NIMH` },
    { value: 'OTHER', label: t`(BATTERY_TYPE)_OTHER` },
];

export const BAGEND_TO_TRAFI_KEY = Object.freeze({
    '@katsastusajankohta': '@katsastusajankohta',
    additional_color: 'lisavari',
    additional_container: 'lisasailio',
    additional_equipment_for_bus_chassis: 'linjaautokorinLisavarusteet',
    additional_equipments: 'lisalaitteet',
    additional_info: 'lisatieto',
    advance_notice: 'ennakkoilmoitus',
    agent: 'asiamies',
    amount: 'maara',
    amount_of_noise_in_vehicle: 'voimakkuusLASisamelu',
    amount_of_noise_passing_by: 'voimakkuusMeluOhiajossa',
    amount_of_noise_standing_still: 'voimakkuusMeluPaikallaan',
    applicant: 'hakija',
    application: 'sovellus',
    assembly_in_eu: 'kokoonpanoEUssa',
    axle: 'akseli',
    axle_group: 'akseliryhma',
    axle_information: 'akselitiedot',
    batch_code: 'erakoodi',
    brand: 'merkkiSelvakielinen',
    breaking: 'jarruttava',
    breaks: 'jarrut',
    bridge_rule_length: 'siltasaantopituus',
    bridge_rule_mass: 'siltasaantomassa',
    build: 'rakenne',
    build_vehicle: 'build_vehicle',
    buyer: 'ostaja',
    buyers: 'ostajat',
    calculated_pressure: 'laskentapaine',
    carburetor_make_model: 'kaasuttimenMerkkiMalli',
    certificate_number: 'todistuksenNumero',
    certificate_type: 'todistuksenTyyppi',
    certificate_types: 'todistuksentyypit',
    change_technical_inspections: 'muutoskatsastukset',
    chassis: 'kori',
    chassis_type: 'korityyppi',
    cockpit_type: 'ohjaamotyyppi',
    coded_remark: 'kooditettuHuomautus',
    color: 'vari',
    commercial_name: 'kaupallinenNimi',
    commissioning_date: 'kayttoonottopvm',
    component: 'komponentti',
    connecting_device: 'kytkentalaite',
    connecting_devices: 'kytkentalaitteet',
    connection_for_steering_device: 'ohjauslaitteenKytkenta',
    connection_mass: 'pvKytkentamassa',
    consumption: 'kulutus',
    consumption_of_electric_energy: 'sahkoenergiankulutus',
    consumption_type: 'kulutuslaji',
    consumptions: 'kulutukset',
    contract_start_date: 'suhteenAlkupvm',
    coupling: 'kytkenta',
    coupling_for_kevyt: 'kevyenKytkenta',
    coupling_information_in_normal_traffic: 'kytkentatiedotNormaaliliikenteessa',
    customer_type: 'asiakaslaji',
    customs_processing_site: 'tullikasittelypaikkakunta',
    d_value: 'dArvo',
    date: 'ajankohta',
    date_for_LKO: 'LKOPvm',
    date_of_decease: 'kuolinpaiva',
    date_of_notice_of_release: 'luovutusilmoituspvm',
    date_of_return: 'palautuspaiva',
    dc_value: 'dcArvo',
    defect: 'vika',
    defect_notice: 'vikatiedot',
    determined_percentage: 'todettuProsentti',
    device: 'laite',
    diameter_for_break_cylinder: 'jarrusylHalkaisija',
    distance_between_axle: 'akselivali',
    distance_between_axles: 'akselivalit',
    door_position: 'ovienSijainti',
    drive_height: 'ajonKorkeus',
    drive_width: 'ajonLeveys',
    driving_route: 'ajoreitti',
    driving_stability_control: 'ajovakaudenHallinta',
    due_date: 'maaraAika',
    electric_hybrid: 'sahkohybridi',
    electric_hybrid_class: 'sahkohybridinluokka',
    emission: 'paasto',
    emission_cleansing: 'pakokaasunPuhdistus',
    emission_level: 'paastotaso',
    emission_type: 'paastolaji',
    emissions: 'paastot',
    end_date_for_relationship: 'suhteenLoppupvm',
    end_date_for_validity: 'voimassaoloPaattyy',
    engine_identification: 'moottorinTunnus',
    engine_volume: 'iskutilavuus',
    environment: 'ymparisto',
    equipment: 'varuste',
    error: 'virhe',
    error_class: 'virheluokka',
    error_code: 'virhekoodi',
    error_meaning: 'virheselite',
    expiry_date: 'voimoloLoppuaika',
    export_registration: 'vientirekisterointi',
    extensive: 'laaja',
    extra_breaks: 'lisajarrut',
    faults: 'viat',
    first_name_subtype: 'etunimiAlayksikko',
    first_registration_date: 'ensirekisterointipvm',
    fitted_with_paired_wheels: 'paripyorillaVarustettu',
    foreign_license_plate: 'ulkomainenRekisteritunnus',
    frame: 'kehys',
    front_axle_distance: 'etaisyysEtuaks',
    front_overhead: 'etuylitys',
    front_overhead_in_turning: 'kaantymisetuylitys',
    front_turning_width: 'kaantymisetuleveys',
    fuel_tank: 'polttoainesailio',
    fuel_type: 'kayttovoima',
    fuel_types: 'kayttovoimat',
    full_length_of_vehicle: 'ajonKokPituus',
    general: 'yleinen',
    group_number: 'ryhmaNro',
    handler: 'kasittelija',
    id: 'tunnus',
    import_country: 'tuontimaa',
    imported_individually: 'yksittainMaahantuotu',
    inspection_date: 'katsastusajankohta',
    inspection_decision: 'katsastuspaatos',
    inspection_for_change_in_spec: 'muutoskatsastus',
    inspection_place: 'katsastuspaikka',
    inspection_pressure_alb: 'albTarkastuspaine',
    inspection_type: 'katsastuslaji',
    insurance_company_code: 'vakuutusyhtiokoodi',
    insurance_company_name: 'vakuutusyhtionNimi',
    insurance_end_date: 'vakuutuksenPaattymispvm',
    insurance_info: 'vakuutustiedot',
    insurance_start_date: 'vakuutuksenAlkupvm',
    insurance_type: 'vakuutustyyppi',
    insured_id: 'vakuutuksenottajanTunnus',
    insured_name: 'vakuutuksenottajanNimi',
    intercooler: 'valijaahdytin',
    is_mandatory: 'pakollisuus',
    is_pulling: 'vetava',
    is_steerable: 'ohjautuva',
    is_steering: 'ohjaava',
    is_weight_reducable: 'kevennettava',
    it_system: 'tietojarjestelma',
    kt: 'kt',
    language_for_conducting_business: 'asiointikieli',
    largest_total_mass_for_combination: 'yhdSuurSallKokmassa',
    last_name_main_unit: 'sukunimiPaayksikko',
    last_odometer_value: 'viimeisinkilometrilukema',
    length: 'pituus',
    length_for_correction: 'oikaisupituus',
    length_of_break_lever: 'jarruvivunPituus',
    length_of_cargo_container: 'kuormakorinPituus',
    length_of_closed_trunk_space: 'umpinaisenTavaratilanPituus',
    length_of_combination: 'yhdistelmanPituus',
    length_of_open_trunk: 'avonaisenTavaratilanPituus',
    licence_plate_of_pulling_vehicle: 'vetoautonRekisteritunnus',
    limit_subtype: 'rajoitusAlalaji',
    limit_type: 'rajoitustieto',
    limiting_terms: 'rajoitustiedot',
    limiting_text: 'rajoitusTeksti',
    limiting_type: 'rajoitusLaji',
    list_for_vakADR: 'vakADRlista',
    load_and_speed_class: 'kuormitusJaNopeusluokka',
    location: 'sijainti',
    location_for_alb_ventt_saato_plate: 'albVenttSaatokilvenSij',
    location_of_release: 'luovutuspaikkakunta',
    location_of_vin_number: 'valmistenumeronSijainti',
    main_topic: 'paakohde',
    main_unit: 'paayksikko',
    make: 'merkki',
    manufacturer_name: 'valmistajanNimi',
    manufacturer_plate_location: 'valmistajanKilvenSijainti',
    marketing_ban: 'markkinointikielto',
    mass: 'mass',
    massa: 'mass',
    mass_inflicted_on_method_of_pulling: 'ppvVetotapKohdMassa',
    mass_with_breaks: 'massaJarruin',
    mass_with_breaks_allowed_by_manufacturer: 'massaJarruinValmSall',
    mass_without_breaks: 'massaJarruitta',
    mass_without_breaks_allowed_by_manufacturer: 'massaJarruittaValmSall',
    material: 'materiaali',
    maximum_allowed_combined_weight_in_road_traffic: 'tieliikSuurSallMassa',
    maximum_allowed_length_for_bridge_rule: 'pvSuurSallSiltasaantopituus',
    maximum_allowed_length_for_correction: 'pvSuurSallOikaisupituus',
    maximum_allowed_mass_for_the_combination: 'yhdistelmanSuurSallMassa',
    maximum_allowed_mass_for_vetopoyta: 'ajonSuurSallVetopoytamassa',
    maximum_allowed_mass_technical_for_towing: 'suurTeknSallHinattavaMassa',
    maximum_allowed_total_weight_in_road_traffic: 'tieliikSuurSallKokmassa',
    maximum_allowed_vetopituus: 'pvSuurSallVetopituus',
    maximum_height_from_the_ground: 'maxkorkeusMaasta',
    maximum_net_power: 'suurinNettoteho',
    maximum_power_for_electric_engine: 'sahkomoottorinsuurinteho',
    maximum_spinning_velocity: 'suurinPyorintanopeus',
    measurements: 'mitat',
    message: 'sanoma',
    message_type: 'sanomatyyppi',
    meter_with_five_digits: 'viisinumeroinenMittari',
    method_for_harnessing_and_amplifying_power_additional_info: 'voimanvalJaTehostamistapaLisatieto',
    method_for_how_power_is_harnessed_and_amplified: 'voimanvalJaTehostamistapa',
    method_of_security_for_meistos: 'meistoksenVarmennus',
    minimum_height_from_ground: 'minkorkeusMaasta',
    model: 'malli',
    module: 'moduuli',
    month: 'kk',
    motor: 'moottori',
    motor_number: 'moottorinNumero',
    must_be_lifted: 'nostettava',
    name: 'nimi',
    next_tech_inspection_end: 'next_tech_inspection_end',
    next_tech_inspection_start: 'next_tech_inspection_start',
    no_odometer: 'eiMatkamittaria',
    noise_standing_still_with_rpm: 'kierroksillaMeluPaikallaan',
    not_suitable_for_pulling_solid_aispv: 'eiSovKiintAisPvVetoon',
    notice_of_release: 'luovutusilmoitus',
    notices_of_release: 'luovutusilmoitukset',
    notified_date: 'ilmoituspvm',
    number_of_axles: 'akselienLkm',
    number_of_cylinders: 'sylintereidenLkm',
    number_of_doors: 'ovienLukumaara',
    number_of_gears_in_transmission: 'vaihteidenLkm',
    number_of_seats: 'istumapaikkojenLkm',
    number_of_standing_up_passengers: 'seisomapaikkojenLkm',
    odometer_value: 'kilometrilukema',
    odometer_value_in_miles: 'mittarilukemaMaileina',
    office_location: 'toimipaikka',
    offset: 'offset',
    only_for_towed_device: 'vainHinattLaittVetoon',
    order_number: 'jarjestysnumero',
    order_of_release: 'luovutusjarjestys',
    own_mass: 'omamassa',
    own_mass_axlegroup_alh: 'omamassaAksryhmaAlh',
    own_mass_for_pulling_type: 'ppvVetotapKohdOmamassa',
    own_mass_kevylh: 'omaMassaKevYlh',
    owner_holder: 'omistajaHaltija',
    owner_holders: 'omistajatHaltijat',
    owner_identification: 'omistajanTunnus',
    ownership_type: 'omistajuustyyppi',
    parking_break: 'seisontajarru',
    password: 'salasana',
    permit_for_transit: 'siirtolupa',
    personal_id: 'hetu',
    phone_number_of_agent: 'kasittelijanPuh',
    place: 'paikka',
    plate: 'rekisteritunnus',
    plate_model: 'kilvenMalli',
    plates_are_missing: 'kilvetKadonneet',
    postal_area: 'postitoimipaikka',
    postal_code: 'postinumero',
    power_steering: 'tehostettuOhjaus',
    previous_plate: 'edellinenRekisteritunnus',
    print_date: 'tulostuspaiva',
    printable: 'tulostettava',
    pull_length: 'vetopituus',
    purpose_of_use: 'kayttotarkoitus',
    radius_for_aisa: 'aisanSade',
    raised_speed_for_engine_running_on_neutral: 'korotettuJoutokayntinopeus',
    range_for_electric_vehicle: 'sahkokaytontoimintasade',
    rear_corner_sivutt_siirt: 'takakulmanSivuttsiirt',
    rear_overhead: 'takaylitys',
    rear_turning_overhead: 'kaantymistakaylitys',
    rear_width_for_turning: 'kaantymistakaleveys',
    recipient: 'vastaanottaja',
    reference_break_power: 'viittJarruvoima',
    registration_cert_1_part: 'rekisterointitod1Osa',
    registration_cert_2_part: 'rekisterointitod2Osa',
    relationship_of_control: 'hallintasuhde',
    replaced_part: 'vaihdettuOsa',
    replaced_parts: 'vaihdetutOsat',
    restriction_for_release123: 'luovutusrajoitus',
    rim_size: 'vannekoko',
    roof_top_cargo: 'kattokuorma',
    row_of_seats: 'penkkirivi',
    s_value: 'sArvo',
    safety_equipment: 'turvavarusteet',
    seats_next_to_driver: 'istuimetKuljVieressa',
    security_equipments: 'turvavaruste',
    separate_connection: 'erilliskytkenta',
    separate_connections: 'erilliskytkennat',
    sequence_number: 'sekvenssinumero',
    severity: 'vakavuus',
    shield_for_underdrive: 'alleajosuoja',
    shields_for_underdrive: 'alleajosuojat',
    side_shield: 'sivusuoja',
    side_shields: 'sivusuojat',
    single_fuel_type: 'yksittaisKayttovoima',
    solely_manufactured_in_finland: 'yksittainSuomessaValmistettu',
    special_condition: 'erikoisehto',
    special_term_part: 'erikoisehdOsaalue',
    special_terms: 'erikoisehdot',
    speed_for_running_neutral: 'joutokayntinopeus',
    start_date_for_limit: 'voimoloAlkuaika',
    start_date_for_validity: 'voimAlkupvm',
    status: 'tila',
    street_address: 'lahiosoite',
    subunit: 'alayksikko',
    supercharger: 'ahdin',
    supercharger_make_model: 'ahtimenMerkkiMalli',
    system_id: 'jarjestelmatunnus',
    target: 'kohde',
    tech_spec: 'tekninen',
    text: 'teksti',
    theoretical_maximum_allowed_total_weight: 'teknSuurSallKokmassa',
    theoretical_maximum_allowed_weight: 'teknSuurSallMassa',
    thickness_of_break_plate: 'jarrulevynPaksuus',
    top_speed: 'huippunopeus',
    total_mass_for_module: 'modulinKokonaismassa',
    total_mass_of_vehicle_in_PV_use: 'autonKokmassaPVkaytossa',
    total_percentage_of_replaced_parts: 'vaihdOsienKokonaispros',
    transmission: 'vaihteisto',
    type: 'tyyppi',
    type_approval_number: 'tyyppihyvaksyntanro',
    type_code: 'tyyppikoodi',
    type_for_chassis_for_load: 'kuormakorinTyyppi',
    type_name: 'tyyppinimi',
    type_of_combustion_system: 'polttojarjtyyppi',
    type_of_release: 'luovutusLaji',
    u_value: 'uArvo',
    usage_rights: 'kayttooikeudet',
    user: 'kayttaja',
    vakADR: 'vakADR',
    variant: 'variantti',
    vehicle_basic_info: 'ajoneuvonPerustiedot',
    vehicle_class: 'ajoneuvoluokka',
    vehicle_group: 'ajoneuvoryhma',
    vehicle_groups: 'ajoneuvoryhmat',
    vehicle_info: 'ajoneuvontiedot',
    vehicle_information: 'ajoneuvonTiedot',
    vehicle_usage: 'ajoneuvonKaytto',
    version: 'versio',
    vin: 'valmistenumero',
    vin_number_location: 'valmnoMeistettySijainti',
    vin_number_location_other: 'valmistenumeronSijaintiMuu',
    volume: 'tilavuus',
    volume_of_closed_body: 'umpikorinTilavuus',
    wheel_break_type: 'pyorajarrunTyyppi',
    wheel_size: 'rengaskoko',
    wheel_type: 'rengaslaji',
    wheels: 'rengas',
    width_at_front_axle: 'leveysEtuaksKohd',
    width_of_load_container: 'kuormakorinLeveys',
    wirings: 'kytkennat',
});

export const BATTERY_BOOLS = [
    'is_water',
    'is_stink',
    'is_fire',
    'is_leak',
    'is_heat',
    'is_fried',
    'is_dent',
    'is_sullen',
    'is_humid',
];
